import { Component, Inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { UserService } from 'src/app/service/user/user.service';
import { RouterParamSeervice } from 'src/app/service/params/params.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CustomValidators } from 'src/app/shared/custom-validator/custom-validators';
import { environment } from 'src/environments/environment';
import { FormRow, KaercherDynamicFormComponent } from '@kaercher/ng-forms';
import { RecaptchaErrorParameters } from 'ng-recaptcha';
import {UrlValidationService} from "../../service/url-validation/url-validation.service";

enum loginHints {
  Google = 'Google',
  Apple = 'Apple',
  Facebook = 'Facebook',
  Empty = '',
}

@Component({
    selector: 'app-registration-form',
    templateUrl: './registration-form.component.html',
    styleUrls: ['./registration-form.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RegistrationFormComponent {
  registrationForm: FormGroup;
  registrationFormConfigurationRows: FormRow[];
  registrationFormConfig: string = 'userdata-commercial';

  loading: boolean;
  isocode: string = 'de-DE';
  redirectUrl: string = '';
  backToApp: boolean = false;
  passwordInvalid: boolean = false;
  fieldTextType: boolean = false;
  repeatPasswordInvalid: boolean = false;
  showRequired: boolean = false;
  passwordTextType: boolean = false;
  repaetPasswordTextType: boolean = false;
  strength: string = 'grey';
  strengthText: string = '';
  showToastError: boolean = false;
  commercial: boolean = false;

  @ViewChild('captchaRef') captchaRef: any;

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    public params: RouterParamSeervice,
    private route: ActivatedRoute,
    public translate: TranslateService,
    private router: Router,
    private urlValidation: UrlValidationService
) {
    this.loading = false;
    this.isocode =  translate.currentLang ?? 'en';
    if(this.isocode === 'en'){
      window.location.href = environment.notFoundUrl;
    }

    this.registrationForm = formBuilder.group(
      {
        email: new FormControl(
          '',
          Validators.compose([Validators.email, Validators.required])
        ),
        password: new FormControl(
          '',
          Validators.compose([
            // 1. Password Field is Required
            Validators.required,
            // 2. check whether the entered password has a number
            CustomValidators.patternValidator(/\d/, { hasNumber: true }),
            // 3. check whether the entered password has upper case letter
            CustomValidators.patternValidator(/[A-Z]/, {
              hasCapitalCase: true,
            }),
            // 4. check whether the entered password has a lower-case letter
            CustomValidators.patternValidator(/[a-z]/, { hasSmallCase: true }),
            // 5. check whether the entered password has a special character
            // [^A-Z0-9] [-._!"`'#%&,:;<>=@{}~\$\(\)\*\+\/\\\?\[\]\^\|]+
            CustomValidators.patternValidator(/[^a-zA-Z\d]/, {
              hasSpecialCharacters: true,
            }),
            // 6. Has a minimum length of 8 characters
            Validators.minLength(8),
          ])
        ),
        repeatPassword: new FormControl(
          '',
          Validators.compose([Validators.required])
        ),
        dataProtectionCheck: new FormControl(false, Validators.requiredTrue),
        newsletterCheck: new FormControl(false),
      },
      {
        // check whether our password and confirm password match
        validator: CustomValidators.passwordMatchValidator,
      }
    );
    this.route.queryParams.subscribe((params) => {
      const validationResult = this.urlValidation.validateRedirectUrl(params['redirectUrl']);
      this.backToApp = validationResult.isValid;
      this.redirectUrl = validationResult.url;
    });
  }
  onSubmit(data: any) {
    this.showRequired = true;
    if (data.status != 'INVALID') {
      this.loading = true;
      this.captchaRef.grecaptcha.execute();
    }
  }
  focusOutPassword() {
    this.passwordInvalid = this.registrationForm.controls['password'].invalid;
    this.repeatPasswordInvalid =
      this.registrationForm.controls['repeatPassword'].invalid;
  }
  togglePassword() {
    this.passwordTextType = !this.passwordTextType;
  }
  toggleNewRepaetPassword() {
    this.repaetPasswordTextType = !this.repaetPasswordTextType;
  }
  onPasswordInput() {
    let failureCount = 0;
    if (this.registrationForm.controls['password'].hasError('minlength')) {
      failureCount++;
    }
    if (
      this.registrationForm.controls['password'].hasError('hasSmallCase') ||
      this.registrationForm.controls['password'].hasError('hasCapitalCase')
    ) {
      failureCount++;
    }
    if (this.registrationForm.controls['password'].hasError('hasNumber')) {
      failureCount++;
    }
    if (
      this.registrationForm.controls['password'].hasError(
        'hasSpecialCharacters'
      )
    ) {
      failureCount++;
    }
    if (this.registrationForm.controls['password'].hasError('required')) {
      failureCount = 5;
    }
    switch (failureCount) {
      case 0:
        this.strength = 'green';
        this.strengthText = this.translate.instant('validation.veryStrong');
        break;
      case 1:
        this.strength = 'orange';
        this.strengthText = this.translate.instant('validation.strong');
        break;
      case 2:
        this.strength = 'orange';
        this.strengthText = this.translate.instant('validation.weak');

        break;
      case 3:
        this.strength = 'red';
        this.strengthText = this.translate.instant('validation.weak');

        break;
      case 4:
        this.strength = 'red';
        this.strengthText = this.translate.instant('validation.veryStrong');

        break;
      case 5:
        this.strength = 'grey';
        this.strengthText = '';

        break;
      default:
        this.strength = 'grey';
        this.strengthText = '';

        break;
    }
  }
  login(login_hint: loginHints) {
    if (this.backToApp) {
        if(!this.redirectUrl.includes("reg_idp")){
          this.redirectUrl = this.redirectUrl + "&reg_idp=MyKaercher"
        }
        window.location.href = this.redirectUrl;

    } else {
      window.location.href = environment.mykaercher + '/' + this.isocode+'?login_hint=' + login_hint;
    }
  }
  onToastErrorHide() {
    this.showToastError = false;
  }

  public resolved(event: string | null, data: any): void {
    grecaptcha.reset();

    this.userService
      .registerUser(
        data.value.email,
        data.value.password,
        data.value.newsletterCheck,
        this.commercial,
        this.isocode,
        event
      )
      .subscribe({
        error: (error) => {
          if (error.status === 0) {
            console.error('An error occurred:', error.error);
          } else if (error.status === 400) {
            let errorBuilder: any = {};
            errorBuilder[error.error.error] = true;
            this.registrationForm.controls[error.error.inputField].setErrors(
              errorBuilder
            );
            this.showRequired = true;
          } else {
            console.error(
              `Backend returned code ${error.status}, body was: `,
              error.error
            );
            console.error(error.error.Message);
            this.showToastError = true;
          }
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
          if (this.redirectUrl != '' && this.backToApp) {
            if(!this.redirectUrl.includes("reg_login_hint")){
              this.redirectUrl = this.redirectUrl +  "&reg_login_hint=" + encodeURIComponent(data.value.email)
            }
            if(!this.redirectUrl.includes("reg_idp")){
              this.redirectUrl = this.redirectUrl + "&reg_idp=MyKaercher"
            }
            this.router.navigate([this.isocode, 'success'], {
              queryParams: { redirectUrl: this.redirectUrl },
            });
          } else {
            this.router.navigate([this.isocode, 'success']);
          }
        },
      });
  }

  public onError(errorDetails: RecaptchaErrorParameters): void {
    grecaptcha.reset();
    console.log(`reCAPTCHA error encountered; details:`, errorDetails);
    this.loading = false;
  }

  protected readonly loginHints = loginHints;
}
